import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowRight, 
  Calendar, 
  User, 
  Tag,
  Clock,
  Eye,
  Loader
} from 'lucide-react'
import apiService from '../../services/api'

const News: React.FC = () => {
  const [news, setNews] = useState([])
  const [categories, setCategories] = useState(['全部'])
  const [selectedCategory, setSelectedCategory] = useState('全部')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false
  })

  useEffect(() => {
    fetchNews()
    fetchCategories()
  }, [selectedCategory])

  const fetchNews = async () => {
    try {
      setLoading(true)
      const params = {
        page: 1,
        limit: 12,
        ...(selectedCategory !== '全部' && { category: selectedCategory })
      }
      const response = await apiService.getNews(params)
      setNews(response.data)
      setPagination(response.pagination)
    } catch (err) {
      setError(err.message)
      // 使用模拟数据作为后备
      setNews(getMockNews())
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await apiService.getNewsCategories()
      setCategories(['全部', ...response.data])
    } catch (err) {
      console.error('获取分类失败:', err)
    }
  }

  const getMockNews = () => [
    {
      _id: 1,
      title: 'CoreTrust手机正式发布，开启Web3手机新时代',
      excerpt: 'DeepLink Limited今日正式发布CoreTrust手机，这是一款基于Web3深度集成的智能手机，集成了熄屏广告、空投助手、数字银行等六大核心功能...',
      author: 'DeepLink团队',
      publishedAt: '2024-01-15T00:00:00.000Z',
      category: '产品发布',
      image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800&h=400&fit=crop',
      views: 1250
    },
    {
      _id: 2,
      title: 'DeepLink完成B轮融资，获得$25M资金支持',
      excerpt: 'DeepLink Limited宣布完成B轮融资，获得$25M资金支持。本轮融资由知名投资机构领投，将用于产品研发、市场推广和团队扩张...',
      author: '投资团队',
      publishedAt: '2024-01-10T00:00:00.000Z',
      category: '融资新闻',
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&h=400&fit=crop',
      views: 980
    },
    {
      _id: 3,
      title: 'Web3硬件安全技术突破，芯片级加密保护用户资产',
      excerpt: 'DeepLink在Web3硬件安全技术方面取得重大突破，成功研发出芯片级加密技术，为用户数字资产提供最高级别的安全保护...',
      author: '技术团队',
      publishedAt: '2024-01-05T00:00:00.000Z',
      category: '技术突破',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop',
      views: 756
    },
    {
      _id: 4,
      title: 'DeepLink与多家Web3项目达成战略合作',
      excerpt: 'DeepLink宣布与多家知名Web3项目达成战略合作，将在CoreTrust手机中集成更多优质应用，为用户提供更丰富的Web3体验...',
      author: '商务团队',
      publishedAt: '2023-12-28T00:00:00.000Z',
      category: '合作新闻',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=400&fit=crop',
      views: 634
    },
    {
      _id: 5,
      title: 'CoreTrust手机获得多项国际安全认证',
      excerpt: 'CoreTrust手机成功通过多项国际安全认证，包括FIDO2、CC EAL5+等，证明其安全性能达到国际先进水平...',
      author: '质量团队',
      publishedAt: '2023-12-20T00:00:00.000Z',
      category: '认证新闻',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=400&fit=crop',
      views: 542
    },
    {
      _id: 6,
      title: 'DeepLink参加CES 2024，展示最新Web3技术',
      excerpt: 'DeepLink参加CES 2024国际消费电子展，展示了最新的Web3技术和CoreTrust手机，吸引了众多参观者和媒体的关注...',
      author: '市场团队',
      publishedAt: '2023-12-15T00:00:00.000Z',
      category: '展会新闻',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=400&fit=crop',
      views: 423
    }
  ]

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
  }

  return (
    <>
      <Helmet>
        <title>新闻中心 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited最新新闻动态，包括产品发布、融资进展、技术突破等信息。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-orange-900 via-red-900 to-pink-900">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                新闻中心
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                了解DeepLink的最新动态和发展进展
              </p>
            </motion.div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-4 justify-center">
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    category === selectedCategory
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* News Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader className="w-8 h-8 animate-spin text-orange-600" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">加载中...</span>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-red-600 dark:text-red-400 mb-4">加载失败: {error}</p>
                <button 
                  onClick={fetchNews}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  重试
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {news.map((article, index) => (
                <motion.article
                  key={article._id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="aspect-video bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-500 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {article.author}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(article.publishedAt).toLocaleDateString('zh-CN')}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {article.views}
                      </div>
                    </div>
                    
                    <Link
                      to={`/news/${article._id}`}
                      className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold transition-colors duration-200"
                    >
                      阅读更多
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </motion.article>
              ))}
              </div>
            )}
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                订阅我们的新闻
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                第一时间获取DeepLink的最新动态和产品信息
              </p>
              <div className="max-w-md mx-auto flex gap-4">
                <input
                  type="email"
                  placeholder="请输入您的邮箱"
                  className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-white/50"
                />
                <button className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
                  订阅
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default News